<template>
  <aside :class="['training-ai-assistant', expanded ? 'is-expanded' : 'is-collapsed']">
    <template v-if="expanded">
      <div class="training-ai-assistant__header">
        <div class="training-ai-assistant__brand">
          <span class="training-ai-assistant__logo">
            <i class="el-icon-magic-stick" />
          </span>
          <span class="training-ai-assistant__title">AI 助手</span>
        </div>
        <el-button type="text"
                   class="training-ai-assistant__close"
                   icon="el-icon-close"
                   title="收起 AI 助手"
                   @click="expanded = false" />
      </div>

      <div ref="messageList"
           class="training-ai-assistant__messages">
        <div v-for="item in messages"
             :key="item.id"
             :class="['training-ai-assistant__message-row', item.role === 'user' ? 'is-user' : 'is-assistant']">
          <div :class="['training-ai-assistant__avatar', item.role === 'user' ? 'is-user' : 'is-assistant']">
            <span v-if="item.role === 'user'">我</span>
            <i v-else
               class="el-icon-magic-stick" />
          </div>
          <div class="training-ai-assistant__bubble-wrap">
            <div :class="['training-ai-assistant__bubble', item.role === 'user' ? 'is-user' : 'is-assistant']">
              <div class="training-ai-assistant__bubble-text">{{ item.content }}</div>
            </div>
            <div class="training-ai-assistant__time">{{ formatTime(item.timestamp) }}</div>
          </div>
        </div>

        <div v-if="replying"
             class="training-ai-assistant__message-row is-assistant">
          <div class="training-ai-assistant__avatar is-assistant">
            <i class="el-icon-magic-stick" />
          </div>
          <div class="training-ai-assistant__bubble-wrap">
            <div class="training-ai-assistant__bubble is-assistant is-loading">
              <i class="el-icon-loading" />
              <span>思考中...</span>
            </div>
          </div>
        </div>
      </div>

      <div v-if="quickPrompts.length"
           class="training-ai-assistant__prompts">
        <button v-for="prompt in quickPrompts"
                :key="prompt"
                type="button"
                class="training-ai-assistant__prompt"
                :disabled="replying"
                @click="sendMessage(prompt)">
          {{ prompt }}
        </button>
      </div>

      <div class="training-ai-assistant__composer">
        <el-input v-model="draft"
                  type="textarea"
                  :rows="3"
                  resize="none"
                  placeholder="输入消息... (Enter 发送，Shift + Enter 换行)"
                  :disabled="replying"
                  @keydown.native="onComposerKeydown" />
        <el-button type="primary"
                   icon="el-icon-s-promotion"
                   class="training-ai-assistant__send"
                   :disabled="!canSend"
                   :loading="replying"
                   @click="sendMessage()">
          发送
        </el-button>
      </div>
    </template>

    <template v-else>
      <button type="button"
              class="training-ai-assistant__expand-btn"
              title="展开 AI 助手"
              @click="expanded = true">
        <i class="el-icon-d-arrow-left" />
      </button>
      <div class="training-ai-assistant__collapsed-logo"
           title="AI 助手">
        <i class="el-icon-magic-stick" />
      </div>
    </template>
  </aside>
</template>

<script>
import { buildWelcomeMessage, mockSendTrainingAiMessage } from '@views/training/utils/training-ai-assistant.mock'

const QUICK_PROMPTS_MAP = {
  'instruction-list': ['如何上架培训？', '怎么筛选零工培训？', '上下架有什么区别？'],
  'instruction-form': ['培训配置要注意什么？', '关联考试有什么要求？'],
  'instruction-bind-history': ['绑定历史怎么看？', '绑定后何时生效？'],
  'instruction-records': ['进度怎么理解？', '导出包含哪些字段？'],
  'instruction-exam-records': ['怎么筛通过率？', '答卷状态有哪些？'],
  'instruction-exam-detail': ['如何看错题？', '得分怎么计算的？'],
  'course-list': ['教程和培训什么关系？'],
  'course-form': ['教程章节怎么组织？'],
  'knowledge-list': ['知识库怎么用？'],
}

export default {
  name: 'TrainingAiAssistantSidebar',
  props: {
    context: {
      type: String,
      default: 'instruction-list',
    },
    pageTitle: {
      type: String,
      default: '',
    },
  },
  data () {
    return {
      expanded: false,
      draft: '',
      replying: false,
      messages: [],
    }
  },
  computed: {
    canSend () {
      return !!this.draft.trim() && !this.replying
    },
    quickPrompts () {
      return QUICK_PROMPTS_MAP[this.context] || ['这个页面能做什么？']
    },
  },
  watch: {
    context: {
      immediate: true,
      handler () {
        this.resetConversation()
      },
    },
    expanded (value) {
      this.$emit('expand-change', value)
    },
    messages () {
      this.$nextTick(this.scrollToBottom)
    },
    replying () {
      this.$nextTick(this.scrollToBottom)
    },
  },
  mounted () {
    this.$emit('expand-change', this.expanded)
  },
  methods: {
    resetConversation () {
      this.messages = [{
        id: `welcome-${Date.now()}`,
        role: 'assistant',
        content: buildWelcomeMessage(this.context),
        timestamp: new Date(),
      }]
      this.draft = ''
      this.replying = false
    },
    formatTime (value) {
      const date = value instanceof Date ? value : new Date(value)
      if (Number.isNaN(date.getTime())) {
        return ''
      }
      const hours = `${date.getHours()}`.padStart(2, '0')
      const minutes = `${date.getMinutes()}`.padStart(2, '0')
      return `${hours}:${minutes}`
    },
    onComposerKeydown (event) {
      if (event.key === 'Enter' && !event.shiftKey) {
        event.preventDefault()
        this.sendMessage()
      }
    },
    scrollToBottom () {
      const el = this.$refs.messageList
      if (el) {
        el.scrollTop = el.scrollHeight
      }
    },
    sendMessage (presetText) {
      const content = (presetText != null ? presetText : this.draft).trim()
      if (!content || this.replying) {
        return
      }

      this.messages.push({
        id: `user-${Date.now()}`,
        role: 'user',
        content,
        timestamp: new Date(),
      })
      this.draft = ''
      this.replying = true

      mockSendTrainingAiMessage({
        message: content,
        context: this.context,
        pageTitle: this.pageTitle,
      }).then((reply) => {
        this.messages.push(reply)
      }).catch(() => {
        this.$message.error('AI 助手暂时不可用，请稍后再试')
      }).finally(() => {
        this.replying = false
      })
    },
  },
}
</script>

<style lang="scss" scoped>
.training-ai-assistant {
  position: fixed;
  top: 58px;
  right: 10px;
  bottom: 28px;
  z-index: 100;
  background: #fff;
  border: 1px solid #ebeef5;
  border-radius: 12px;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.08);
  overflow: hidden;
  transition: width 0.2s ease, box-shadow 0.2s ease;

  &.is-expanded {
    display: flex;
    flex-direction: column;
    width: 400px;
  }

  &.is-collapsed {
    display: flex;
    flex-direction: column;
    align-items: center;
    width: 48px;
    padding: 16px 0;
    gap: 12px;
  }

  &__header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    height: 56px;
    padding: 0 16px;
    border-bottom: 1px solid #ebeef5;
    flex-shrink: 0;
    background: #fff;
  }

  &__brand {
    display: flex;
    align-items: center;
    gap: 10px;
    min-width: 0;
  }

  &__logo {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 28px;
    height: 28px;
    border-radius: 8px;
    background: linear-gradient(135deg, #a855f7, #3b82f6);
    color: #fff;
    flex-shrink: 0;

    i {
      font-size: 14px;
    }
  }

  &__title {
    font-size: 15px;
    font-weight: 600;
    color: #303133;
  }

  &__close {
    padding: 6px;
    color: #909399;
  }

  &__messages {
    flex: 1;
    overflow-y: auto;
    padding: 16px;
  }

  &__message-row {
    display: flex;
    gap: 10px;
    margin-bottom: 16px;

    &.is-user {
      flex-direction: row-reverse;
    }
  }

  &__avatar {
    width: 28px;
    height: 28px;
    border-radius: 50%;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    font-size: 12px;
    font-weight: 600;

    &.is-user {
      background: #409eff;
      color: #fff;
    }

    &.is-assistant {
      background: linear-gradient(135deg, #a855f7, #3b82f6);
      color: #fff;

      i {
        font-size: 14px;
      }
    }
  }

  &__bubble-wrap {
    max-width: 320px;
    min-width: 0;
  }

  &__bubble {
    border-radius: 16px;
    padding: 10px 12px;
    font-size: 13px;
    line-height: 1.65;
    word-break: break-word;

    &.is-user {
      background: #409eff;
      color: #fff;
    }

    &.is-assistant {
      background: #f3f4f6;
      color: #303133;
    }

    &.is-loading {
      display: inline-flex;
      align-items: center;
      gap: 8px;
      color: #909399;
    }
  }

  &__bubble-text {
    white-space: pre-wrap;
  }

  &__time {
    margin-top: 4px;
    padding: 0 4px;
    font-size: 11px;
    color: #c0c4cc;
  }

  &__prompts {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
    padding: 0 16px 12px;
    border-top: 1px solid #f2f3f5;
    flex-shrink: 0;
  }

  &__prompt {
    border: 1px solid #dcdfe6;
    background: #fff;
    color: #606266;
    border-radius: 999px;
    padding: 4px 10px;
    font-size: 12px;
    line-height: 18px;
    cursor: pointer;
    transition: background-color 0.15s ease, border-color 0.15s ease;

    &:hover:not(:disabled) {
      background: #ecf5ff;
      border-color: #b3d8ff;
      color: #409eff;
    }

    &:disabled {
      cursor: not-allowed;
      opacity: 0.6;
    }
  }

  &__composer {
    display: flex;
    align-items: flex-end;
    gap: 10px;
    padding: 12px 16px 16px;
    border-top: 1px solid #ebeef5;
    flex-shrink: 0;
    background: #fff;
  }

  &__send {
    flex-shrink: 0;
  }

  &__expand-btn {
    border: none;
    background: transparent;
    color: #7c3aed;
    width: 32px;
    height: 32px;
    border-radius: 8px;
    cursor: pointer;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    transition: background-color 0.15s ease;

    &:hover {
      background: #f5f3ff;
    }
  }

  &__collapsed-logo {
    width: 32px;
    height: 32px;
    border-radius: 8px;
    background: linear-gradient(135deg, #a855f7, #3b82f6);
    color: #fff;
    display: inline-flex;
    align-items: center;
    justify-content: center;

    i {
      font-size: 16px;
    }
  }
}
</style>

<style lang="scss">
.training-ai-assistant__composer {
  .el-textarea__inner {
    font-size: 13px;
    line-height: 1.5;
  }
}
</style>



// WEBPACK FOOTER //
// src/views/training/components/TrainingAiAssistantSidebar.vue